import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Truck, Users, TrendingUp, Mail, BarChart3, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';

const DealerLandingPage = () => {
  const howItWorks = [
    {
      step: 1,
      title: 'Share Your Link',
      description: 'Get your unique application link and share it on your website, social media, or marketing campaigns.'
    },
    {
      step: 2,
      title: 'Leads Take the Application',
      description: 'Potential buyers answer AI-powered questions about their RV needs, budget, and travel plans.'
    },
    {
      step: 3,
      title: 'Get Qualified Leads',
      description: 'Receive instant notifications with intent scores so you know who\'s ready to buy.'
    },
    {
      step: 4,
      title: 'Close More Deals',
      description: 'Our automated email sequence keeps leads warm while you focus on closing sales.'
    }
  ];

  const features = [
    {
      icon: <Truck className="w-8 h-8 text-[#F2A900]" />,
      title: 'Your Branded Application',
      description: 'Get a personalized application link for your dealership that captures qualified leads 24/7.'
    },
    {
      icon: <Users className="w-8 h-8 text-[#F2A900]" />,
      title: 'Exclusive Leads',
      description: 'Every lead from your application link belongs to you. No sharing, no competition.'
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-[#F2A900]" />,
      title: 'Intent Scoring',
      description: 'AI-powered scoring tells you which leads are ready to buy today.'
    },
    {
      icon: <Mail className="w-8 h-8 text-[#F2A900]" />,
      title: 'Automated Follow-ups',
      description: '7-day email sequence keeps leads engaged while you focus on closing.'
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-[#F2A900]" />,
      title: 'Lead Dashboard',
      description: 'Manage, filter, and export leads. Track status from new to sold.'
    },
    {
      icon: <Shield className="w-8 h-8 text-[#F2A900]" />,
      title: 'Inventory Management',
      description: 'Add your RV inventory or embed your existing inventory page.'
    }
  ];

  const plans = [
    {
      id: 'trial',
      name: '7-Day Trial',
      price: '$7',
      period: 'for 7 days',
      description: 'Try it for a week',
      features: ['Full feature access', 'Unlimited leads', 'Auto-converts to monthly'],
      highlight: true,
      badge: 'START HERE'
    },
    {
      id: 'monthly',
      name: 'Monthly',
      price: '$49.99',
      period: '/month',
      description: 'Perfect for getting started',
      features: ['All features included', 'Cancel anytime', 'Priority support'],
      highlight: false
    },
    {
      id: 'yearly',
      name: 'Yearly',
      price: '$499.99',
      period: '/year',
      description: 'Save $100 annually',
      features: ['All features included', '2 months free', 'Priority support'],
      highlight: false,
      badge: 'BEST VALUE'
    },
    {
      id: 'lifetime',
      name: 'Lifetime',
      price: '$999.99',
      period: 'one-time',
      description: 'Forever access',
      features: ['All features forever', 'No recurring fees', 'VIP support'],
      highlight: false
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dealer-landing-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#004E86] to-[#00619D] text-white py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block bg-[#F2A900] text-black text-sm font-bold px-4 py-1 rounded-full mb-6">
            FOR RV DEALERS
          </span>
          <h1 
            className="text-4xl md:text-6xl font-bold uppercase tracking-tight mb-6"
            style={{fontFamily: 'Barlow Condensed, sans-serif'}}
          >
            Turn Website Visitors Into<br />
            <span className="text-[#F2A900]">Qualified Buyers</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto">
            Our AI-powered application captures leads while you sleep. Get your own branded link and start converting today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/dealer/register?plan=trial">
              <Button
                size="lg"
                className="bg-[#F2A900] text-black hover:bg-[#D99700] font-bold uppercase tracking-wide px-8 py-4 text-lg"
                data-testid="hero-signup-btn"
              >
                Start 7-Day Trial - $7
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 
            className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-[#004E86] text-center mb-4"
            style={{fontFamily: 'Barlow Condensed, sans-serif'}}
          >
            Everything You Need to Capture & Convert
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            RV Match AI gives you the tools to generate leads, nurture prospects, and close more deals.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow"
                data-testid={`feature-card-${index}`}
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-[#004E86] mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 
            className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-[#004E86] text-center mb-4"
            style={{fontFamily: 'Barlow Condensed, sans-serif'}}
          >
            How It Works
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Start capturing qualified leads in minutes. Here's how simple it is:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((item) => (
              <div 
                key={item.step}
                className="relative text-center"
                data-testid={`how-it-works-step-${item.step}`}
              >
                {/* Step number */}
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#004E86] to-[#00619D] flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">{item.step}</span>
                </div>
                {/* Connector line (hidden on last item and mobile) */}
                {item.step < 4 && (
                  <div className="hidden lg:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-[#004E86] to-[#F2A900]" />
                )}
                <h3 className="text-xl font-bold text-[#004E86] mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 
            className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-[#004E86] text-center mb-4"
            style={{fontFamily: 'Barlow Condensed, sans-serif'}}
          >
            Simple, Transparent Pricing
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Start with our $7 trial and upgrade when you're ready. No hidden fees, no surprises.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan) => (
              <div 
                key={plan.id}
                className={`relative rounded-2xl p-6 transition-all ${
                  plan.highlight 
                    ? 'bg-gradient-to-br from-[#004E86] to-[#00619D] text-white shadow-2xl scale-105 border-4 border-[#F2A900]' 
                    : 'bg-gray-50 border-2 border-gray-200 hover:border-[#F2A900]'
                }`}
                data-testid={`pricing-${plan.id}`}
              >
                {plan.badge && (
                  <span className={`absolute -top-3 left-1/2 transform -translate-x-1/2 text-xs font-bold px-3 py-1 rounded-full ${
                    plan.highlight ? 'bg-[#F2A900] text-black' : 'bg-[#004E86] text-white'
                  }`}>
                    {plan.badge}
                  </span>
                )}
                <div className="text-center mb-6">
                  <h3 className={`text-xl font-bold mb-2 ${plan.highlight ? 'text-white' : 'text-[#004E86]'}`}>
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    <span className={`text-sm ${plan.highlight ? 'text-white/80' : 'text-gray-500'}`}>
                      {plan.period}
                    </span>
                  </div>
                  <p className={`text-sm mt-2 ${plan.highlight ? 'text-white/80' : 'text-gray-500'}`}>
                    {plan.description}
                  </p>
                </div>
                
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <Check className={`w-5 h-5 flex-shrink-0 ${plan.highlight ? 'text-[#F2A900]' : 'text-green-500'}`} />
                      <span className={`text-sm ${plan.highlight ? 'text-white/90' : 'text-gray-600'}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <Link to={`/dealer/register?plan=${plan.id}`} className="block">
                  <Button 
                    className={`w-full font-bold ${
                      plan.highlight 
                        ? 'bg-[#F2A900] text-black hover:bg-[#D99700]' 
                        : 'bg-[#004E86] text-white hover:bg-[#003d6b]'
                    }`}
                    data-testid={`select-${plan.id}-btn`}
                  >
                    Get Started
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#F2A900]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-black mb-4"
            style={{fontFamily: 'Barlow Condensed, sans-serif'}}
          >
            Ready to Transform Your Lead Generation?
          </h2>
          <p className="text-lg text-black/80 mb-8">
            Join dealers across the country who are using RV Match AI to capture more qualified buyers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/dealer/register?plan=trial">
              <Button 
                size="lg"
                className="bg-[#004E86] text-white hover:bg-[#003d6b] font-bold uppercase tracking-wide px-8 py-4"
                data-testid="cta-signup-btn"
              >
                Start Your 7-Day Trial
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#004E86] text-white py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white/70 text-sm">
            © {new Date().getFullYear()} RV Match AI. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default DealerLandingPage;
